import { useRef } from 'react';

import { useProject } from '@contexts/Project.context';

/**
 * The tools that can take the map over, in priority order: the first one whose
 * flag is set is the one reported. A boundary edit also sets `interactionsDisabled`,
 * so it has to come first to keep the more specific reason.
 *
 * `owner` is the name a control passes to exclude itself — see the hook below.
 */
const MAP_OWNERS = [
	{
		owner: 'boundaryEdit',
		flag: 'boundaryEditActive',
		reason: 'Confirm or cancel the boundary edit first',
	},
	{
		owner: 'areaDraw',
		flag: 'interactionsDisabled',
		reason: 'Finish or cancel the area selection first',
	},
	{
		owner: 'measurement',
		flag: 'measurementMode',
		reason: 'Exit measurement mode first',
	},
];

const NOT_BUSY = Object.freeze({ busy: false, reason: '' });

const resolveBusy = (state, self) => {
	const active = MAP_OWNERS.find(({ owner, flag }) => owner !== self && state[flag]);
	return active ? { busy: true, reason: active.reason } : NOT_BUSY;
};

/**
 * Answers "is another tool currently in charge of the map?" for the controls
 * that must not start a competing map action while one is running.
 *
 * The tools and the flags that carry their state are listed in `MAP_OWNERS`
 * above. Gating on one flag per control is how the two detection modes drifted
 * apart, so every such control asks here instead.
 *
 * A control that is itself one of those tools passes its own `owner` name and is
 * told about everyone else. Measurement needs this: its toggle is the only way
 * back out of the mode, so gating it on the flag the mode itself sets would
 * disable the exit and strand the user in it.
 *
 * Detect-in-area, Train AI and the model picker deliberately stay on
 * `!toolBarVisible`: they must also be unavailable while a single image is open,
 * which none of the flags here covers.
 *
 * `busy`/`reason` are the render-time answer, fine for disabling a control. A
 * handler that awaits before touching the map has to call `getBusy()` again
 * after its last await — the destructured values are the ones from the render
 * the click started on, and a tool can take the map over while a request is in
 * flight. Call it, don't depend on it: its identity changes every render.
 *
 * @param {Object} [options]
 * @param {string} [options.owner] This control's own entry in `MAP_OWNERS`,
 *   excluded from the answer.
 * @returns {{ busy: boolean, reason: string, getBusy: () => { busy: boolean, reason: string } }}
 *   `reason` is '' when not busy, so it can be passed straight to a Tooltip
 *   title (antd renders none for '').
 */
export const useMapToolBusy = ({ owner } = {}) => {
	const { boundaryEditActive, interactionsDisabled, measurementMode } = useProject();

	const state = resolveBusy({ boundaryEditActive, interactionsDisabled, measurementMode }, owner);

	const latest = useRef(state);
	latest.current = state;

	return { ...state, getBusy: () => latest.current };
};
